import React, { memo, useState, useEffect, Fragment, useRef, forwardRef, useImperativeHandle } from "react";

// others
import dayjs from "dayjs";
import { Card, Col, Empty, Form, Row, Space, Typography, message } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useForm, useWatch } from "react-hook-form";
import { getConfigData } from "../../../ducks/actions";
import {
  apiDateFormat,
  disableSingleField,
  filterDescriptionLabel,
  filterPaymentPayload,
  numberWithCommas,
  payment_modes,
} from "../../utils";
import { getOrgByID } from "../../../../Maintenance/Tickets/ducks/actions";
import { convertToDayjs, convertToTitleCase, DecimalUtils } from "../../../../../libs/utils";
import { DateField, InputField, ReactSelectField } from "../../../../../atoms/FormElement";
import { InputNumberFieldV2 as InputNumberField } from "../../../../../atoms/FormElement/InputNumberField";
import { Popup } from "../../../../../atoms/Popup";
import Button from "../../../../../molecules/Button";
import { usePreviewScheduleAmendment } from "../api/v1/usePreviewScheduleAmendment";

const { Text } = Typography;

let initialState = {
  visible: true,
  paymentSchedule: [],
  totalSum: 0,
  initialTotalSum: 0,
  bankList: [],
  triggerIndex: null,
  showAutoFillPopup: false,
  numberOfInstallments: 0,
};

var _ = require("lodash");

interface PaymentScheduleAmendmentsProps {
  onSubmit: (data: any) => void;
  leaseTerms?: any;
  paymentScheduleAmendments?: string;
}

export type PaymentScheduleFormHandle = {
  submitForm: () => void;
};
const index = forwardRef<PaymentScheduleFormHandle, PaymentScheduleAmendmentsProps>(
  ({ onSubmit, leaseTerms, paymentScheduleAmendments }, ref) => {
    // hooks initialization
    // redux states
    const { bookingDetails } = useSelector((state: any) => state.newBooking);
    const configData = useSelector((state: any) => state.dashboard.configData);
    const { loading, error, data, previewSchedule } = usePreviewScheduleAmendment(leaseTerms);
    const docNoDebounceTimeout = useRef<NodeJS.Timeout | null>(null);

    let isDepositPaid = bookingDetails?.leaseStatusHistory?.find((e) => e.status === "deposit_paid");

    const typeOfRenewal = useParams();
    const dispatch: any = useDispatch();

    let newRenewalType = typeOfRenewal["*"];

    const {
      watch,
      control,
      register,
      setValue,
      getValues,
      resetField,
      handleSubmit,
      reset,
      formState: { errors },
    } = useForm();

    // states
    let [
      {
        visible,
        paymentSchedule,
        totalSum,
        initialTotalSum,
        bankList,
        triggerIndex,
        showAutoFillPopup,
        numberOfInstallments,
      },
      setState,
    ] = useState(initialState);

    const updateState = (state: {}) => setState((prevState: any) => ({ ...prevState, ...state }));

    useEffect(() => {
      if (!configData?.bankList) {
        dispatch(getConfigData(["bankList", "requiredBookingDocumentsList"]));
      }
      if (triggerIndex === null) return;

      const item = paymentSchedule[triggerIndex];
      const docNo = getValues(`documentNo-${triggerIndex}`);
      const bank = getValues(`bankName-${triggerIndex}`);

      if (item.description === "rent_installment" && docNo && bank?.value) {
        updateState({
          showAutoFillPopup: true,
        });
      } else {
        updateState({
          showAutoFillPopup: false,
        });
      }
    }, [triggerIndex]);

    const autoFillRemaining = (startIndex: number, startDocNo: string, bank: { label: string; value: string }) => {
      let num = parseInt(startDocNo, 10);
      if (isNaN(num)) return;

      for (let i = startIndex + 1; i < paymentSchedule.length; i++) {
        const item = paymentSchedule[i];
        if (item.description === "rent_installment") {
          num += 1;
          setValue(`documentNo-${i}`, String(num));
          setValue(`bankName-${i}`, bank);
        }
      }
    };
    let isRenewal = bookingDetails?.leaseType === "renewal" || bookingDetails?.leaseType === "extension";

    useEffect(() => {
      if (leaseTerms || paymentScheduleAmendments === "paymentScheduleAmendments") {
        let leaseTerm = null;

        if (paymentScheduleAmendments === "paymentScheduleAmendments") {
          leaseTerm = {
            leaseStartDate: apiDateFormat(bookingDetails?.leaseTerms?.leaseStartDate),
            leaseEndDate: apiDateFormat(bookingDetails?.leaseTerms?.leaseEndDate),
          };
        } else if (isRenewal) {
          leaseTerm = leaseTerms;
        } else {
          leaseTerm = {
            leaseStartDate: leaseTerms?.leaseTerms?.leaseStartDate,
            leaseEndDate: leaseTerms?.leaseTerms?.leaseEndDate,
          };
        }

        previewSchedule({
          leaseId: bookingDetails?.leaseId,
          leaseTerms: leaseTerm,
        });
      }
    }, [leaseTerms]);

    useEffect(() => {
      if (data?.result) {
        updateState({
          paymentSchedule: filterDescriptionLabel([...data?.result], false, [], false, newRenewalType),
          numberOfInstallments: bookingDetails?.leaseTerms?.noOfCheques,
        });
      }
    }, [data]);

    useEffect(() => {
      if (bookingDetails && bookingDetails?.orgId) {
        dispatch(getOrgByID(bookingDetails?.orgId));
      }
    }, []);

    useEffect(() => {
      if (configData && configData?.bankList && Object.keys(configData?.bankList)?.length > 0) {
        const bankList = configData?.bankList || {};
        const formattedBankList = Object?.entries(bankList as Record<string, string>).map(([key, label]) => ({
          label,
          value: label.toLowerCase().replace(/\s+/g, "_"),
        }));
        updateState({
          bankList: formattedBankList,
        });
      }
    }, [configData?.bankList]);

    // Watch for all form fields changes
    const allValues = useWatch({
      control,
    });

    useEffect(() => {
      const subscription = watch((value, { name }) => paymentSchedule?.length > 0 && handleFields(name, value[name]));
      return () => subscription.unsubscribe();
    }, [watch, paymentSchedule]);

    useEffect(() => {
      const initialTotal = getSumTotal();
      updateState({ totalSum: initialTotal, initialTotalSum: initialTotal });
    }, [paymentSchedule]);

    useEffect(() => {
      const newTotalSum = getSumTotal();
      if (newTotalSum !== totalSum) {
        updateState({ totalSum: newTotalSum });
      }
    }, [allValues]);

    const sumRentInstallmentAmounts = (amounts: Array<string | number>): number => {
      if (!amounts.length) return 0;
      return DecimalUtils.toNumber(DecimalUtils.add(amounts));
    };

    const calculateVatAmount = (amount: number | string, vatRate: string | number): number => {
      const rate = DecimalUtils.toNumber(vatRate);
      if (!rate) return 0;
      return DecimalUtils.toNumber(DecimalUtils.multiply(amount, DecimalUtils.multiply(rate, 0.01)));
    };

    const calculateTotalWithVat = (amount: number | string, vatRate: string | number): number => {
      return DecimalUtils.toNumber(DecimalUtils.add([amount, calculateVatAmount(amount, vatRate)]));
    };

    const handleFields = (name: string, value: any) => {
      const splitName = name && name?.split("-");
      const index = splitName && parseFloat(splitName[1]);
      const copy: Array<any> = [...paymentSchedule];

      switch (splitName && splitName[0]) {
        case "vat":
          const actualAmount = DecimalUtils.toNumber(getValues(`actualAmount-${index}`)?.toString()?.replace(",", ""));

          if (actualAmount) {
            setValue(`totalAmount-${index}`, calculateTotalWithVat(actualAmount, value));
          }

          break;
        case "actualAmount":
          const vat = DecimalUtils.toNumber(getValues(`vat-${index || 0}`));

          if (vat) {
            setValue(`totalAmount-${index}`, calculateTotalWithVat(value, vat));
          } else setValue(`totalAmount-${index}`, DecimalUtils.toNumber(value));
          break;
        case "modeOfPayment":
          if (value?.value === "cash") {
            register(`documentNo-${splitName[1]}`, {
              value: "",
              disabled: true,
            });
            register(`bankName-${splitName[1]}`, {
              value: "",
              disabled: true,
            });
            register(`chequeDate-${splitName[1]}`, {
              value: "",
              disabled: true,
            });
            resetField(`chequeDate-${splitName[1]}`);
            resetField(`bankName-${splitName[1]}`, { defaultValue: "" });
            resetField(`documentNo-${splitName[1]}`, { defaultValue: "" });
          } else {
            setValue(
              `chequeDate-${splitName[1]}`,
              dayjs(copy[splitName[1]]?.chequeDate || copy[splitName[1]]?.dueDate),
            );
          }

          copy[splitName[1]].modeOfPayment = value.value;
          updateState({ paymentSchedule: copy });
          break;
      }
    };

    const checkRentInstallmentTotalAmount = (items) => {
      const rentInstallments = items.filter((transaction) => transaction.description === "rent_installment");
      const amounts = rentInstallments.map((transaction) => transaction.actualAmount);
      return sumRentInstallmentAmounts(amounts);
    };

    const getSumTotal = (): number => {
      const amounts: string[] = [];
      if (!paymentSchedule?.length) return 0;

      paymentSchedule.forEach((item, index) => {
        if (
          item?.description == "rent_installment" &&
          getValues(`actualAmount-${index}`) &&
          (item?.chequeStatus === "pending" || item?.chequeStatus === "paid")
        ) {
          let valString = getValues(`actualAmount-${index}`)?.toString();
          let val = valString?.replace(/,/g, "");
          amounts.push(val);
        }
      });

      return sumRentInstallmentAmounts(amounts);
    };

    const showError = () => {
      message.error(`Sum of rent installments must be equal to contract rent. Please correct and try again`);
      updateState({ visible: false });
      return;
    };

    const submitHandler = handleSubmit((data) => {
      let leaseId = bookingDetails?.leaseId;
      let dataCopy = {
        leaseId: leaseId,
        paymentSchedule: filterPaymentPayload(data, paymentSchedule),
      };

      if (dataCopy?.paymentSchedule?.length > 0) {
        const totalAmount =
          bookingDetails?.leaseType === "extension"
            ? bookingDetails?.bookingTerms?.contractRent
            : bookingDetails?.bookingTerms?.renewalAmount || bookingDetails?.bookingTerms?.annualBaseRent;
        let calcSum = checkRentInstallmentTotalAmount(dataCopy?.paymentSchedule);

        const start = dayjs(bookingDetails?.leaseTerms?.leaseStartDate);
        const end = dayjs(bookingDetails?.leaseTerms?.leaseEndDate);
        const isMultiYear = end.diff(start, "year", true) >= 1;

        if (isMultiYear) {
          if (!DecimalUtils.equals(calcSum, initialTotalSum)) {
            showError();
            return;
          }
        } else {
          if (!DecimalUtils.equals(calcSum, totalAmount)) {
            showError();
            return;
          }
        }

        onSubmit(dataCopy);
      }

      try {
      } catch (error) {
        updateState({ visible: false });
        console.log(error);
        message.error(error.response?.data?.message);
      }
    });

    // Expose submit method to parent
    useImperativeHandle(ref, () => ({
      submitForm: () => submitHandler(),
    }));

    const textStyle = { fontSize: "12px" };
    const isPayment = paymentSchedule.length > 0;

    const vatInPercentage = (data) => {
      const totalAmount = DecimalUtils.toNumber(data?.totalAmount);
      const actualAmount = DecimalUtils.toNumber(data?.actualAmount);
      const vat = DecimalUtils.toNumber(data?.vat);

      let totalVat = 0;

      if (actualAmount > 0) {
        if (totalAmount === actualAmount) {
          totalVat = 100 * ((totalAmount + vat) / actualAmount - 1);
        } else {
          totalVat = 100 * (totalAmount / actualAmount - 1);
        }
      }

      return numberWithCommas(DecimalUtils.toNumber(DecimalUtils.toString(totalVat)));
    };

    const calCulateTotalAmount = (data) => {
      if (data?.actualAmount === data?.totalAmount) {
        return numberWithCommas(data?.totalAmount + data?.vat || 0);
      } else {
        return numberWithCommas(data?.totalAmount || "");
      }
    };

    return (
      <>
        {!isPayment && !loading && (
          <Row style={{ justifyContent: "center" }}>
            <Empty
              //   width="200px"
              //   height="200px"
              style={{ alignContent: "center" }}
            />
          </Row>
        )}

        {loading && <Card loading={loading}></Card>}
        {
          <Form
            layout="vertical"
            name="bookingform"
            scrollToFirstError
            onFinish={submitHandler}
            className="bf-main view-booking edit-payment-schedule"
          >
            {isPayment && paymentSchedule?.length > 0 && (
              <Row gutter={[2, 2]} style={{ letterSpacing: "1.2px", marginBottom: "8px" }}>
                <Col span={3}>
                  <Text style={textStyle} className="fontWeight500 ag-default mb-1">
                    DESCRIPTION
                  </Text>
                </Col>
                <Col span={2}>
                  <Text style={textStyle} className="fontWeight500 ag-default mb-2">
                    AMOUNT
                  </Text>
                </Col>
                <Col span={2}>
                  <Text style={textStyle} className="fontWeight500 ag-default mb-2">
                    VAT %
                  </Text>
                </Col>
                <Col span={2}>
                  <Text style={textStyle} className="fontWeight500 ag-default mb-2">
                    TOTAL AMOUNT
                  </Text>
                </Col>
                {
                  <>
                    <Col span={3}>
                      <Text style={textStyle} className="fontWeight500 ag-default mb-2">
                        PAYMENT MODE
                      </Text>
                    </Col>
                    <Col span={2}>
                      <Text style={textStyle} className="fontWeight500 ag-default mb-2">
                        DOCUMENT NO
                      </Text>
                    </Col>
                    <Col span={2}>
                      <Text style={textStyle} className="fontWeight500 ag-default mb-2">
                        BANK NAME
                      </Text>
                    </Col>
                  </>
                }
                <Col span={3}>
                  <Text style={textStyle} className="fontWeight500 ag-default mb-2">
                    DUE DATE
                  </Text>
                </Col>
                <Col span={3}>
                  <Text style={textStyle} className="fontWeight500 ag-default mb-2">
                    INVOICE DATE
                  </Text>
                </Col>

                {
                  <>
                    <Col span={3}>
                      <Text style={textStyle} className="fontWeight500 ag-default mb-2">
                        CHEQUE DATE
                      </Text>
                    </Col>

                    <Col span={2}>
                      <Text style={textStyle} className="fontWeight500 ag-default mb-2">
                        STATUS
                      </Text>
                    </Col>
                  </>
                }
              </Row>
            )}

            {paymentSchedule.map((item: any, index: number) => {
              const modeOfPayment = item?.modeOfPayment
                ? payment_modes.filter((ele: any) => ele.value.toLowerCase() === item?.modeOfPayment.toLowerCase())
                : payment_modes[1];
              return (
                <Fragment key={item?.pdcId}>
                  <Row gutter={2} style={{ position: "relative" }}>
                    <Col span={3}>
                      <InputField
                        disabled={true}
                        control={control}
                        // isRequired={true}
                        // rules={{ required: "required" }}
                        fieldname={`description-${index}`}
                        initValue={convertToTitleCase(item?.descriptionLabel)}
                        // validate={errors[`description-${index}`] && "error"}
                      />
                    </Col>

                    <Col span={2}>
                      <InputNumberField
                        control={control}
                        isRequired={true}
                        format="currency"
                        inputNumberProps={{ min: 1 }}
                        rules={{ required: "required" }}
                        fieldname={`actualAmount-${index}`}
                        defaultValue={item?.actualAmount > 0 ? DecimalUtils.toNumber(item?.actualAmount) : 0}
                        disabled={disableSingleField(item?.chequeStatus, item?.description) || isDepositPaid}
                      />
                    </Col>

                    <Col span={2}>
                      <InputNumberField
                        control={control}
                        isRequired={true}
                        inputNumberProps={{ min: 0, max: 100, precision: 2 }}
                        fieldname={`vat-${index}`}
                        rules={{ required: "required", min: { value: 0, message: "VAT must be at least 0" }, max: { value: 100, message: "VAT cannot exceed 100" } }}
                        defaultValue={vatInPercentage(item)}
                        disabled={disableSingleField(item?.chequeStatus, item?.description) || isDepositPaid}
                      />
                    </Col>

                    <Col span={2}>
                      <InputNumberField
                        disabled={true}
                        control={control}
                        isRequired={true}
                        format="currency"
                        rules={{ required: "required" }}
                        fieldname={`totalAmount-${index}`}
                        defaultValue={
                          item?.actualAmount === item?.totalAmount
                            ? DecimalUtils.toNumber(DecimalUtils.add([item?.totalAmount || 0, item?.vat || 0]))
                            : DecimalUtils.toNumber(item?.totalAmount || 0)
                        }
                      />
                    </Col>

                    {
                      <>
                        <Col span={3}>
                          <ReactSelectField
                            control={control}
                            isRequired={true}
                            initValue={modeOfPayment}
                            selectOption={payment_modes}
                            rules={{ required: "required" }}
                            fieldname={`modeOfPayment-${index}`}
                            validate={errors[`modeOfPayment-${index}`] && "error"}
                            iProps={{
                              isDisabled:
                                (bookingDetails?.directDebit == true && item?.description == "rent_installment") ||
                                isDepositPaid,
                            }}
                          />
                        </Col>

                        <Col span={2}>
                          <InputField
                            control={control}
                            initValue={convertToTitleCase(item?.documentNo)}
                            fieldname={`documentNo-${index}`}
                            // validate={errors[`documentNo-${index}`] && "error"}
                            disabled={
                              item?.modeOfPayment === "cash" ||
                              disableSingleField(item?.chequeStatus, item?.description) ||
                              isDepositPaid
                            }
                            onChange={(e) => {
                              const val = e?.target?.value;
                              setValue(`documentNo-${index}`, val);

                              if (docNoDebounceTimeout.current) {
                                clearTimeout(docNoDebounceTimeout.current);
                              }

                              docNoDebounceTimeout.current = setTimeout(() => {
                                const bank = getValues(`bankName-${index}`);
                                if (
                                  bank?.value &&
                                  item.description === "rent_installment" &&
                                  numberOfInstallments !== item?.installmentNumber
                                ) {
                                  updateState({ triggerIndex: index });
                                }
                              }, 1000);
                            }}
                          />
                        </Col>

                        <Col span={2}>
                          <ReactSelectField
                            control={control}
                            initValue={{
                              label: item?.bankName,
                              value: item?.bankName,
                            }}
                            selectOption={bankList || []}
                            fieldname={`bankName-${index}`}
                            // validate={errors[`bankName-${index}`] && "error"}
                            placeholder={"Select Bank"}
                            iProps={{
                              isDisabled: item?.modeOfPayment === "cash" || isDepositPaid,
                            }}
                            onChange={(e) => {
                              setValue(`bankName-${index}`, e);
                              const docNo = getValues(`documentNo-${index}`);
                              if (docNo && item.description === "rent_installment" && numberOfInstallments !== index) {
                                updateState({
                                  triggerIndex: index,
                                });
                              }
                            }}
                          />
                        </Col>
                      </>
                    }

                    <Col span={3}>
                      <DateField
                        control={control}
                        isRequired={true}
                        rules={{ required: "required" }}
                        fieldname={`dueDate-${index}`}
                        validate={errors[`dueDate-${index}`] && "error"}
                        initValue={item?.dueDate ? dayjs(item?.dueDate, "YYYY-MM-DD") : dayjs()}
                        disabled={disableSingleField(item?.chequeStatus, item?.description) || isDepositPaid}
                      />
                    </Col>

                    <Col span={3}>
                      <DateField
                        control={control}
                        isRequired={true}
                        rules={{ required: "required" }}
                        fieldname={`invoiceDate-${index}`}
                        validate={errors[`invoiceDate-${index}`] && "error"}
                        initValue={convertToDayjs(item?.invoiceDate)}
                        disabled={disableSingleField(item?.chequeStatus, item?.description) || isDepositPaid}
                      />
                    </Col>

                    {
                      <>
                        <Col span={3}>
                          <DateField
                            control={control}
                            fieldname={`chequeDate-${index}`}
                            // validate={errors[`chequeDate-${index}`] && "error"}
                            initValue={
                              item?.chequeDate || bookingDetails?.leaseTerms?.dueDate || item?.dueDate
                                ? dayjs(
                                    item?.chequeDate || bookingDetails?.leaseTerms?.dueDate || item?.dueDate, //added fixes 8266
                                    "YYYY-MM-DD"
                                  )
                                : dayjs()
                            }
                            disabled={
                              item?.modeOfPayment === "cash" ||
                              disableSingleField(item?.chequeStatus, item?.description) ||
                              isDepositPaid
                            }
                          />
                        </Col>
                        <Col span={2}>
                          <InputField
                            control={control}
                            initValue={item?.chequeStatus}
                            fieldname={`paymentStatus-${index}`}
                            disabled={true}
                          />
                        </Col>
                      </>
                    }
                  </Row>
                </Fragment>
              );
            })}

            <Row style={{ marginTop: "30px" }}>
              <Col span={3}>{totalSum > 0 && <p className="totalsum-text">Total Rent </p>}</Col>
              <Col span={3}>
                {totalSum > 0 && <p className="totalsum-amount">{"AED" + " " + numberWithCommas(totalSum)}</p>}
              </Col>
            </Row>

            {isPayment && (
              <Row gutter={24} style={{ marginTop: "30px" }}>
                {bookingDetails?.bookingTerms?.annualBaseRent > 0 && (
                  <>
                    <Col span={24} className="display-amount">
                      {bookingDetails?.leaseType === "extension" ? (
                        <>
                          <p className="amount">{`AED ${numberWithCommas(
                            bookingDetails?.bookingTerms?.contractRent
                          )} / `}</p>
                          <p className="year">for {bookingDetails?.bookingTerms?.numberOfRenewalTerms} months</p>
                        </>
                      ) : (
                        <>
                          <p className="amount">{`AED ${numberWithCommas(
                            bookingDetails?.bookingTerms?.renewalAmount || bookingDetails?.bookingTerms?.annualBaseRent
                          )} / `}</p>
                          <p className="year">Year</p>
                        </>
                      )}
                    </Col>
                  </>
                )}
              </Row>
            )}

            <Popup
              visibility={showAutoFillPopup}
              titleColor={{ color: "#FFFFFF", fontWeight: 600 }}
              onCancel={() => {
                updateState({
                  showAutoFillPopup: false,
                  triggerIndex: null,
                });
              }}
              title="Auto-populate remaining document numbers?"
              content={
                <Space align="center" style={{ width: "100%", justifyContent: "center" }} size="large">
                  <Button
                    label="NO"
                    className="bf-discard-button"
                    onClick={() => {
                      updateState({
                        showAutoFillPopup: false,
                        triggerIndex: null,
                      });
                    }}
                  />
                  <Button
                    label="YES"
                    className="cc-fotter_next-button"
                    onClick={() => {
                      const docNo = getValues(`documentNo-${triggerIndex}`);
                      const bank = getValues(`bankName-${triggerIndex}`);
                      autoFillRemaining(triggerIndex, docNo, bank);
                      updateState({
                        showAutoFillPopup: false,
                        triggerIndex: null,
                      });
                    }}
                  />
                </Space>
              }
            />
          </Form>
        }
      </>
    );
  }
);

export default memo(index);
